import React from 'react';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router';
import ComparisonTable from './ComparisonTable';
import AdvantagesSection from './AdvantagesSection';
import BillingSection from './BillingSection';
import NavBar from '../AppBar/AppBar';

interface AdsPageLayoutProps {
  // Meta
  metaDescription: string;
  tiktokPixelId: string;
  facebookPixelId: string;
  
  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroDescription: string;
  heroButtonText: string;
  heroButtonLink: string;
  
  // Comparison Table
  comparisonData: any[];
  leftColumnTitle: string;
  rightColumnTitle: string;
  bottomRowData: {
    left: string;
    middle: string;
    right: string;
  };
  
  // Why Choose Section
  whyChooseTitle: string;
  whyChooseDescription: string;
  
  // Advantages Section
  advantagesTitle: string;
  advantages: Array<{
    icon: string;
    alt: string;
    title: string;
    description: string;
  }>;
  
  // Billing Section
  billingTitle: string;
  billingDescription: string;
  currentSituationImage: string;
  teachrImage: string;
}

const AdsPageLayout: React.FC<AdsPageLayoutProps> = ({
  metaDescription,
  tiktokPixelId,
  facebookPixelId,
  heroTitle,
  heroSubtitle,
  heroDescription,
  heroButtonText,
  heroButtonLink,
  comparisonData,
  leftColumnTitle,
  rightColumnTitle,
  bottomRowData,
  whyChooseTitle,
  whyChooseDescription,
  advantagesTitle,
  advantages,
  billingTitle,
  billingDescription,
  currentSituationImage,
  teachrImage
}) => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <meta name="description" content={metaDescription} />
        {/* landings publicitaires : hors de Google, réservées aux campagnes */}
        <meta name="robots" content="noindex, follow" />
        {/* TikTok Pixel */}
        <script>
          {`!function (w, d, t) {
            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
            var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
            ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
            ttq.load('${tiktokPixelId}');
            ttq.page();
            }(window, document, 'ttq');`}
        </script>

        {/* Meta Pixel Code */}
        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${facebookPixelId}');
          fbq('track', 'PageView');
          `}</script>
      </Helmet>
      
      <div className="bg-white">
        <NavBar index={8}/>
        
        {/* Hero Section */}
        <div className="bg-homePage bg-no-repeat bg-cover lg:h-screen md:px-10 px-2 w-full mt-20">
          <div className="md:w-1/2 md:pt-16 pt-8">
            <h1 className="text-first font-extrabold xl:text-[40px] text-[30px]">
              {heroTitle}
            </h1>
            <p className="text-first font-extrabold xl:text-[40px] text-[30px]">
              {heroSubtitle}
            </p>
            <p className="text-first xl:text-[20px] text-[17px] font-bold mt-6">
              {heroDescription}
            </p>
            <button
              className="md:w-4/5 w-full mx-auto border text-white bg-first text-base uppercase rounded-full py-2 font-extrabold shadow-md xl:mt-20 mt-10"
              onClick={() => navigate(heroButtonLink)}
            >
              {heroButtonText}
            </button>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="w-full z-50 md:px-10 px-4 space-y-4 pb-5">
          <div className="md:flex block md:gap-x-8 mt-2 pb-10 md:space-y-0 space-y-4"></div>
          <ComparisonTable
            dataList={comparisonData}
            leftColumnTitle={leftColumnTitle}
            rightColumnTitle={rightColumnTitle}
            bottomRowData={bottomRowData}
          />
        </div>

        {/* Content Sections */}
        <div className="w-full z-50 lg:px-16 md:px-10 px-4 md:space-y-8 space-y-4 pb-10">
          {/* Why Choose Section */}
          <div className="md:space-y-10 space-y-4 pb-4">
            <h2 className="font-extrabold xl:text-[36px] text-[26px] text-first text-center">
              {whyChooseTitle}
            </h2>
            <p className="text-[#808080] xl:text-[24px] lg:text-[20px] text-[18px]">
              {whyChooseDescription}
            </p>
          </div>

          {/* Advantages Section */}
          <AdvantagesSection
            title={advantagesTitle}
            description=""
            advantages={advantages}
          />

          {/* Billing Section */}
          <BillingSection
            title={billingTitle}
            description={billingDescription}
            currentSituationImage={currentSituationImage}
            teachrImage={teachrImage}
          />
        </div>
      </div>
    </>
  );
};

export default AdsPageLayout; 